import { forwardRef, useEffect, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";

import { UnifiedPartnerSelector } from "./UnifiedPartnerSelector";
import {
  classifyPrefilledPartnerEmails,
  createOptions,
  createSelectedItems,
  handleBlur,
  handleKeyDown,
  handlePaste,
  handleSelectionChange,
  isOptionEqualToValue,
} from "./utils";

import type { UnifiedPartnerSelectorRef } from "./UnifiedPartnerSelector";
import type {
  NetworkGroup,
  NetworkMember,
  PartnerSelectionItem,
} from "../../types";
import type { ShipmentVisibility } from "../VisibilityRadioGroup/VisibilityRadioGroup";

interface UnifiedPartnerSelectorContainerProps {
  selectedConfirmedPartnerEmails: string[];
  selectedGroups: string[];
  selectedNetworkInviteEmails: string[];
  isAllPartnersSelected: boolean;
  onConfirmedPartnerEmailsChange: (emails: string[]) => void;
  onSelectedGroupsChange: (groups: string[]) => void;
  onNetworkInviteEmailsChange: (emails: string[]) => void;
  onAllPartnersSelectedChange: (isSelected: boolean) => void;
  confirmedPartners: NetworkMember[];
  availableGroups: NetworkGroup[];
  pendingInviteEmails: string[];
  allowGroups: boolean;
  loadingPartners?: boolean;
  loadingGroups?: boolean;
  loadingPendingInvites?: boolean;
  partnersError?: string | null;
  groupsError?: string | null;
  shipmentVisibility: ShipmentVisibility;
}

export const UnifiedPartnerSelectorContainer = forwardRef<
  UnifiedPartnerSelectorRef,
  UnifiedPartnerSelectorContainerProps
>((props, ref) => {
  const {
    selectedConfirmedPartnerEmails,
    selectedGroups,
    selectedNetworkInviteEmails,
    isAllPartnersSelected,
    onConfirmedPartnerEmailsChange,
    onSelectedGroupsChange,
    onNetworkInviteEmailsChange,
    onAllPartnersSelectedChange,
    confirmedPartners,
    availableGroups,
    pendingInviteEmails,
    allowGroups,
    loadingPartners = false,
    loadingGroups = false,
    loadingPendingInvites = false,
    partnersError = null,
    groupsError = null,
    shipmentVisibility,
  } = props;

  const posthog = usePostHog();
  const [inputValue, setInputValue] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [lastTypedValue, setLastTypedValue] = useState("");

  // A prefilled address that no longer matches a confirmed partner belongs in the
  // invite bucket: as a confirmed partner it has no carrier account to grant access
  // to, which used to fail the whole post. Held until the partner list resolves,
  // otherwise a genuine partner is downgraded to an invite on the first render. An
  // errored fetch leaves an empty list that means nothing, so it is left alone too.
  useEffect(() => {
    if (loadingPartners || partnersError || isAllPartnersSelected) {
      return;
    }

    const { confirmedPartnerEmails, networkInviteEmails } =
      classifyPrefilledPartnerEmails({
        selectedConfirmedPartnerEmails,
        selectedNetworkInviteEmails,
        confirmedPartners,
      });

    // Nothing moved, so skip the write rather than hand the parent new array
    // identities on every render.
    if (
      confirmedPartnerEmails.length === selectedConfirmedPartnerEmails.length
    ) {
      return;
    }

    onConfirmedPartnerEmailsChange(confirmedPartnerEmails);
    onNetworkInviteEmailsChange(networkInviteEmails);
  }, [
    loadingPartners,
    partnersError,
    isAllPartnersSelected,
    selectedConfirmedPartnerEmails,
    selectedNetworkInviteEmails,
    confirmedPartners,
    onConfirmedPartnerEmailsChange,
    onNetworkInviteEmailsChange,
  ]);

  // Convert current selections to display items
  const selectedItems = createSelectedItems({
    isAllPartnersSelected,
    selectedConfirmedPartnerEmails,
    selectedGroups,
    selectedNetworkInviteEmails,
    confirmedPartners,
    availableGroups,
    allowGroups,
  });

  // Create options for autocomplete
  const options = createOptions({
    confirmedPartners,
    availableGroups,
    pendingInviteEmails,
    allowGroups,
  });

  const handleSelectionChangeWrapper = (
    newSelections: (string | PartnerSelectionItem)[],
  ) => {
    // Counts picks from the dropdown only. An email typed by hand arrives here
    // as a raw string even when it matches an outstanding invite, and that is a
    // different interaction than choosing the invite we surfaced.
    const pendingInvitesAdded = newSelections.filter(
      (selection) =>
        typeof selection === "object" &&
        selection.type === "email" &&
        pendingInviteEmails.some(
          (email) => email.toLowerCase() === selection.email.toLowerCase(),
        ) &&
        !selectedItems.some((item) => isOptionEqualToValue(item, selection)),
    ).length;

    if (pendingInvitesAdded > 0) {
      posthog.capture(ClientAppEvent.CREATE_SHIPMENT_PENDING_INVITE_SELECTED, {
        pending_invites_added: pendingInvitesAdded,
        shipment_visibility: shipmentVisibility,
      });
    }

    handleSelectionChange(newSelections, {
      selectedItems,
      confirmedPartners,
      allowGroups,
      onConfirmedPartnerEmailsChange,
      onSelectedGroupsChange,
      onNetworkInviteEmailsChange,
      onAllPartnersSelectedChange,
    });
  };

  const handleInputChange = (value: string) => {
    // Track what user is typing for validation later
    if (value.trim()) {
      setLastTypedValue(value.trim());
    } else {
      // Clear last typed value when input becomes empty
      setLastTypedValue("");
    }

    setInputValue(value);

    // Clear error when user starts typing
    if (emailError) {
      setEmailError(null);
    }
  };

  const handleKeyDownWrapper = (event: React.KeyboardEvent) => {
    handleKeyDown(event, {
      inputValue,
      confirmedPartners,
      selectedConfirmedPartnerEmails,
      selectedNetworkInviteEmails,
      onConfirmedPartnerEmailsChange,
      onNetworkInviteEmailsChange,
      onInputValueChange: handleInputChange,
      onErrorChange: setEmailError,
    });
  };

  const handleBlurWrapper = () => {
    handleBlur({
      inputValue,
      confirmedPartners,
      selectedConfirmedPartnerEmails,
      selectedNetworkInviteEmails,
      onConfirmedPartnerEmailsChange,
      onNetworkInviteEmailsChange,
      onInputValueChange: handleInputChange,
      onErrorChange: setEmailError,
    });
  };

  const handlePasteWrapper = (event: React.ClipboardEvent) => {
    handlePaste(event, {
      inputValue,
      confirmedPartners,
      selectedConfirmedPartnerEmails,
      selectedNetworkInviteEmails,
      onConfirmedPartnerEmailsChange,
      onNetworkInviteEmailsChange,
      onInputValueChange: handleInputChange,
      onErrorChange: setEmailError,
    });
  };

  // Show alert if no confirmed partners for certain cases
  const hasNoConfirmedPartners = confirmedPartners.length === 0;
  const hasAllPartnersSelected = selectedItems.some(
    (item) => item.type === "all_partners",
  );
  const shouldShowEmptyNetworkAlert =
    hasNoConfirmedPartners &&
    !hasAllPartnersSelected &&
    selectedItems.length === 0;

  return (
    <UnifiedPartnerSelector
      ref={ref}
      selectedItems={selectedItems}
      options={options}
      inputValue={inputValue}
      lastTypedValue={lastTypedValue}
      onSelectionChange={handleSelectionChangeWrapper}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDownWrapper}
      onBlur={handleBlurWrapper}
      onPaste={handlePasteWrapper}
      confirmedPartners={confirmedPartners}
      pendingInviteCount={pendingInviteEmails.length}
      shouldShowEmptyNetworkAlert={shouldShowEmptyNetworkAlert}
      hasAllPartnersSelected={hasAllPartnersSelected}
      loadingPartners={loadingPartners}
      loadingGroups={loadingGroups}
      loadingPendingInvites={loadingPendingInvites}
      partnersError={partnersError}
      groupsError={groupsError}
      emailError={emailError}
      shipmentVisibility={shipmentVisibility}
    />
  );
});
